import React, { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

/**
 * StripePaymentForm (V2 - using PaymentElement)
 *
 * Props:
 *  - isProcessing    : boolean — parent is in a loading state
 *  - onPaymentSuccess: function(paymentIntentId) — called after Stripe confirms payment
 *  - onPaymentError  : function(errorMessage) — called if Stripe confirmation fails
 *  - setPayProcessing: function(boolean) — to control the parent's loading spinner
 *
 * Flow:
 *  1. Elements wrapper in Quote.jsx provides stripe and elements context with clientSecret.
 *  2. PaymentElement renders Card, Apple Pay, Google Pay natively.
 *  3. User clicks "Place Order" in Quote.jsx, which triggers the hidden submit button here.
 *  4. handleSubmit calls stripe.confirmPayment().
 */
export default function StripePaymentForm(props) {
  var isProcessing = props.isProcessing;
  var onPaymentSuccess = props.onPaymentSuccess;
  var onPaymentError = props.onPaymentError;
  var setPayProcessing = props.setPayProcessing;

  var stripe = useStripe();
  var elements = useElements();

  var _err = useState('');
  var errorMsg = _err[0];
  var setErrorMsg = _err[1];

  async function handleSubmit(e) {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setPayProcessing(true);
    setErrorMsg('');

    // confirmPayment will automatically trigger validation in PaymentElement
    var { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        // Return to the exact same page so Quote.jsx can intercept the URL params
        return_url: window.location.href.split('?')[0]
      }
    });

    if (error) {
      setPayProcessing(false);
      var msg = error.message || 'Payment failed. Please try again.';
      setErrorMsg(msg);
      if (onPaymentError) onPaymentError(msg);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Payment succeeded immediately
      if (onPaymentSuccess) onPaymentSuccess(paymentIntent.id);
    } else {
      // For any other status (e.g. requires_action, processing), wait or handle error
      setPayProcessing(false);
      var unkMsg = 'Payment requires further action or is processing.';
      setErrorMsg(unkMsg);
      if (onPaymentError) onPaymentError(unkMsg);
    }
  }

  return (
    <form onSubmit={handleSubmit} className='cap-stripe-form'>
      <PaymentElement id="payment-element" onChange={function() {
        setErrorMsg('');
        if (onPaymentError) onPaymentError('');
      }} />
      {errorMsg && (
        <span className='cap-err cap-stripe-err' style={{ marginTop: '10px', display: 'block' }}>
          {errorMsg}
        </span>
      )}
      <p className='cap-stripe-note' style={{ marginTop: '15px' }}>
        Payment details are encrypted and processed securely by Stripe. PCI-DSS SAQ A compliant.
      </p>
      
      {/* Hidden submit button — triggered by the parent "Place order" button */}
      <button
        id='cap-stripe-submit'
        type='submit'
        disabled={!stripe || !elements}
        style={{ display: 'none' }}
        aria-hidden='true'
      />
    </form>
  );
}
